// 2) Third-party library imports
import {
	createAction,
	createSelector,
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';
import { get, isEqual, isUndefined, update } from 'lodash-es';

// 3a) Constants
import { customersATBCalculationOptions } from 'src/constants/customersSortAndFilters';
import { CUSTOMER_PORTAL } from 'src/constants/roleNames';

// 3d) Utility functions
import { DynamicObject } from 'src/utils/commonInterfaces';

// 3e) Redux actions & types
import { CustomerPortalInformation, CustomerPortalState } from './types';
import { Customer } from '../customers/types';
import type { RootState } from '../storeTypes';
import { getSelectedUserCompany } from '../companies/companySlice';

const actionPrefix = 'customerPortal';

export const initialState: CustomerPortalState = {
	loading: false,
	customerPortalData: undefined,
	errorMessages: [],
	atbType: get(customersATBCalculationOptions, `${0}.value`),
	requestPaymentPlanData: {
		loading: false,
		errorMessages: [],
		invoices: [],
		paymentPlans: []
	}
};

const customerPortalSlice = createSlice({
	name: actionPrefix,
	initialState,
	reducers: {
		getCustomerPortalDataAction: {
			reducer: (
				state,
				_action: PayloadAction<{
                    customerId: string,
                    isUsingCloudImportType: boolean,
                }>
			) => {
				state.loading = true;
				state.errorMessages = [];
			},
			prepare: (
				customerId: string,
				isUsingCloudImportType: boolean,
			) => ({
				payload: {
					customerId,
					isUsingCloudImportType
				}
			})
		},
		getCustomerPortalDataSuccessAction: (
			state,
			action: PayloadAction<Customer>
		) => {
			state.loading = false;
			state.customerPortalData = action.payload;
		},
		getCustomerPortalDataFailureAction: (state, action: PayloadAction<string[]>) => {
			state.loading = false;
			state.customerPortalData = undefined;
			state.errorMessages = action.payload;
		},
		getCustomerPortalRequestPaymentPlanDataAction: {
			reducer: (
				state,
				_action: PayloadAction<DynamicObject>
			) => {
				state.requestPaymentPlanData.loading = true;
				state.requestPaymentPlanData.errorMessages = [];
			},
			prepare: (
				payload: DynamicObject
			) => ({
				payload
			})
		},
		getCustomerPortalRequestPaymentPlanDataSuccessAction: (
			state,
			action: PayloadAction<{
				invoices: CustomerPortalState['requestPaymentPlanData']['invoices'],
				paymentPlans: CustomerPortalState['requestPaymentPlanData']['paymentPlans']
			}>
		) => {
			state.requestPaymentPlanData.loading = false;
			state.requestPaymentPlanData.invoices = action.payload.invoices;
			state.requestPaymentPlanData.paymentPlans = action.payload.paymentPlans;
		},
		getCustomerPortalRequestPaymentPlanDataFailureAction: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.requestPaymentPlanData.loading = false;
			state.requestPaymentPlanData.errorMessages = action.payload;
			state.requestPaymentPlanData.invoices = [];
			state.requestPaymentPlanData.paymentPlans = [];
		}
	}
});

const { actions, reducer } = customerPortalSlice;

// Actions
export const {
	getCustomerPortalDataAction,
	getCustomerPortalDataSuccessAction,
	getCustomerPortalDataFailureAction,
	getCustomerPortalRequestPaymentPlanDataAction,
	getCustomerPortalRequestPaymentPlanDataSuccessAction,
	getCustomerPortalRequestPaymentPlanDataFailureAction
} = actions;

// Selectors
export const getCustomerPortalState = (state: RootState): CustomerPortalState => state.customerPortal;
export const getRequestPaymentPlanData = createSelector(
	getCustomerPortalState,
	(customerPortal) => customerPortal.requestPaymentPlanData
);
export const getCustomerPortalLoading = createSelector(
	getCustomerPortalState,
	(customerPortal) => customerPortal.loading
);
export const getCustomerPortalData = createSelector(
	getCustomerPortalState,
	(customerPortal) => customerPortal.customerPortalData
);
export const getCustomerPortalInformation = createSelector(
	getSelectedUserCompany,
	(selectedUserCompany) => {
		const customerPortalInformation: CustomerPortalInformation = {
			EnableCustomerPortal: false,
			CustomerId: undefined,
		};
		if (!isUndefined(selectedUserCompany)) {
			const { Company, Role, User } = selectedUserCompany;
			const isCustomerPortalEnabled = get(Company, 'EnableCustomerPortal', false);
			const customerId = get(User, 'CustomerId', undefined);
			const isStudent = isEqual(get(Role, 'Name', ''), CUSTOMER_PORTAL);
			
			customerPortalInformation.EnableCustomerPortal = isCustomerPortalEnabled && isStudent && !isUndefined(customerId);
			customerPortalInformation.CustomerId = customerId;
		}

		return customerPortalInformation;
	}
);

// Reducer
export default reducer;